import { COUNTRY } from '../../../utils/constants'
import { isCountryUSA, numberFormatted } from '../../../utils/fns'
import { convertDecimalToInteger } from '../../../utils/validatorWrapper'
import {
  FOUNDATION_TYPE,
  HIDDEN_FIELD_VALUE,
  HOLDOWN_SUBTYPE,
  HOLDOWN_TYPE,
  MATERIAL_TYPE,
  POST_BASE_MATERIAL_TYPE,
  POST_BASE_SUBTYPE,
} from './constants'

export const calculationURL = '/pfd/v2/calculations'
export const calculationHoldownURL = '/pfd/v2/calculations/holdowns'
export const calculationAnchorURL = '/pfd/v2/calculations/anchors'
export const calculationPostBaseURL = '/pfd/v2/calculations/postbase'
export const calculationCNHoldownURL = '/pfd/v2/calculations/cn-holdown'
export const calculationCNPostBaseURL = '/pfd/v2/calculations/cn-postbase'

export const formatUrl = (input) => {
  const {
    holdownDesign,
    foundationDesign,
    holdownSubType,
    postBaseDesign,
    settings: { country },
  } = input

  if (country === COUNTRY.CANADA) {
    if (holdownDesign) {
      return calculationCNHoldownURL
    } else {
      return calculationCNPostBaseURL
    }
  }

  if (holdownDesign && foundationDesign && holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
    return calculationHoldownURL
  } else if (
    holdownDesign &&
    foundationDesign &&
    holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR
  ) {
    return calculationURL
  } else if (
    holdownDesign &&
    !foundationDesign &&
    holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR
  ) {
    return calculationHoldownURL
  } else if (postBaseDesign) {
    return calculationPostBaseURL
  } else {
    return calculationAnchorURL
  }
}

export const formatArrayValue = (arrValue) => {
  return arrValue.map((i) => i.split(',').map(Number)).flat()
}

export const formatRequest = (input) => {
  const {
    postBaseDesign,
    holdownDesign,
    foundationDesign,
    upliftLoadASD,
    downloadASD,
    lateralF1,
    lateralF2,
    postBaseSubType,
    postBaseFastenersType,
    corrosionResistance,
    loadType,
    holdownSubType,
    lumberSpecies,
    wallThickness,
    installOverSheathing,
    holdownFastenersType,
    compressiveStrength,
    anchorageOption,
    raisedFloor,
    materialType,
    twoPourInstall,
    geometryType,
    geometrySubType,
    postBaseGeometryType,
    postBaseGeometrySubType,
    postBaseMaterialType,
    postBaseLumberSpecies,
    postBaseLoadType,
    settings: { country, buildingCode },
    holdownType,
    postBaseType,
    foundationType,
    upliftDuration,
    factoredUpliftLRFD,
    factoredDownloadLRFD,
    minStemwallThickness,
    downloadDuration,
    postWidth,
    postThickness,
    installationType,
  } = input

  if (country === COUNTRY.CANADA) {
    const commonLoadDemand = {
      buildingCode,
      upliftDuration: numberFormatted(upliftDuration),
      factoredUpliftLRFD: numberFormatted(factoredUpliftLRFD),
      showAllModels: true,
    }

    if (holdownDesign) {
      return {
        loadDemand: {
          ...commonLoadDemand,
          loadType:
            holdownType !== HOLDOWN_TYPE.CAST_IN_PLACE
              ? HIDDEN_FIELD_VALUE
              : numberFormatted(loadType),
          corrosionResistance: formatArrayValue(corrosionResistance),
          minStemwallThickness: numberFormatted(minStemwallThickness),
        },
        holdownDesign: {
          holdownType: numberFormatted(holdownType),
          lumberSpecies: numberFormatted(lumberSpecies),
          postWidth: numberFormatted(postWidth),
          postThickness: numberFormatted(postThickness),
          holdownFastenersType: formatArrayValue(holdownFastenersType),
          installationType:
            holdownType !== HOLDOWN_TYPE.CAST_IN_PLACE
              ? HIDDEN_FIELD_VALUE
              : numberFormatted(installationType),
        },
      }
    }

    if (postBaseDesign) {
      return {
        loadDemand: {
          ...commonLoadDemand,
          lateralF1: numberFormatted(lateralF1),
          lateralF2:
            foundationType !== FOUNDATION_TYPE.WOOD
              ? numberFormatted(lateralF2)
              : HIDDEN_FIELD_VALUE,
          factoredDownloadLRFD:
            foundationType !== FOUNDATION_TYPE.WOOD
              ? numberFormatted(factoredDownloadLRFD)
              : HIDDEN_FIELD_VALUE,
          downloadDuration:
            foundationType !== FOUNDATION_TYPE.WOOD
              ? numberFormatted(downloadDuration)
              : HIDDEN_FIELD_VALUE,
          corrosionResistance: formatArrayValue(corrosionResistance),
        },
        postBaseDesign: {
          postBaseType: numberFormatted(postBaseType),
          lumberSpecies: numberFormatted(postBaseLumberSpecies),
          postWidth: numberFormatted(postWidth),
          postThickness: numberFormatted(postThickness),
          postBaseFastenersType: formatArrayValue(postBaseFastenersType),
        },
        foundationDesign: {
          foundationType: numberFormatted(foundationType),
          minStemwallThickness: numberFormatted(minStemwallThickness),
        },
      }
    }
  }

  if (postBaseDesign) {
    return {
      loadDemand: {
        showAllModels: true,
        buildingCode,
        upliftLoadASD: convertDecimalToInteger(upliftLoadASD),
        lateralF1: convertDecimalToInteger(lateralF1),
        ...(postBaseSubType === POST_BASE_SUBTYPE.POST_INSTALLED &&
        postBaseMaterialType === POST_BASE_MATERIAL_TYPE.WOOD
          ? { lateralF2: 0, downloadASD: 0 }
          : {
              lateralF2: convertDecimalToInteger(lateralF2),
              downloadASD: convertDecimalToInteger(downloadASD),
            }),
        ...(postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE && {
          loadType: convertDecimalToInteger(postBaseLoadType),
        }),
      },
      postBaseDesign: {
        postBaseSubType: convertDecimalToInteger(postBaseSubType),
        postBaseFastenersType: formatArrayValue(postBaseFastenersType),
        lumberSpecies: numberFormatted(postBaseLumberSpecies),
        materialType: postBaseMaterialType,
        corrosionResistance: formatArrayValue(corrosionResistance),
        ...(postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE && {
          geometrySubType: convertDecimalToInteger(postBaseGeometrySubType),
          ...(!isCountryUSA(country) && {
            geometryType: convertDecimalToInteger(postBaseGeometryType),
          }),
        }),
      },
    }
  }

  let loadDemandObj = {
    loadDemand: {
      showAllModels: true,
      upliftLoadASD: convertDecimalToInteger(upliftLoadASD),
      buildingCode,
    },
  }
  if (foundationDesign) {
    loadDemandObj.loadDemand = Object.assign({}, loadDemandObj.loadDemand, {
      loadType: loadType,
    })
  }

  let holdownDesignObj = null
  let foundationDesignObj = null

  if (holdownDesign) {
    holdownDesignObj = {
      holdownDesign: {
        holdownSubType: holdownSubType,
        holdownFastenersType: formatArrayValue(holdownFastenersType),
        holdownFastenersModel: "0",
        lumberSpecies: lumberSpecies,
        wallThickness: wallThickness,
      },
    }
    if (holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
      holdownDesignObj.holdownDesign = Object.assign({}, holdownDesignObj.holdownDesign, {
        installOverSheathing: installOverSheathing,
      })
    }
  }

  if (foundationDesign) {
    foundationDesignObj = {
      foundationDesign: {
        anchorageOption: anchorageOption,
        materialType: materialType,
        geometryType: geometryType,
        geometrySubType: geometrySubType,
      },
    }
    if (materialType === MATERIAL_TYPE.CONCRETE) {
      foundationDesignObj.foundationDesign = Object.assign(
        {},
        foundationDesignObj.foundationDesign,
        {
          compressiveStrength: compressiveStrength,
          twoPourInstall: twoPourInstall,
        },
      )
    }
    if (holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
      foundationDesignObj.foundationDesign = Object.assign(
        {},
        foundationDesignObj.foundationDesign,
        {
          raisedFloor: raisedFloor,
        },
      )
    }
    // if (geometryType != '0' && geometryType != '1' && geometryType != '3') {
    //   foundationDesignObj.foundationDesign = Object.assign( {}, foundationDesignObj.foundationDesign, {
    //     geometrySubType: geometrySubType
    //   })
    // }
  }

  let result = Object.assign({}, loadDemandObj, holdownDesignObj, foundationDesignObj)

  return result
  // return {
  //   input: result
  // }
}
